<template>
  <div ref="containerRef">
    <template v-if="!props.editing">
      <div class="display-mode" @click="startEditing">
        <div class="display-label display-label--name" :style="{ color: readModelDisplayColor }">
          <v-icon size="12" class="mr-1">
            {{ linkedSchema?.type === SchemaType.DOMAIN_EVENT ? 'mdi-note-outline' : 'mdi-eye-outline' }}
          </v-icon>
          {{ readModelName || 'Select schema' }}
        </div>
        <div
          v-if="showDescription"
          :ref="setDescriptionRef"
          class="query-description"
          :class="{ 'query-description--empty': !linkedSchema?.description }"
          :data-placeholder="linkedSchema?.description ? '' : 'No description'"
        >
          {{ linkedSchema?.description || '' }}
        </div>
        <div class="d-flex justify-space-between align-center ga-2 w-100">
          <select-entity
            :entityId="props.query.schemaData?.schema?.entityId"
            @onSelectEntity="(entity) => onSelectEntity(entity)"
          >
            <v-chip density="compact" size="small" class="fit-content entity-chip">
              <v-icon start size="small">mdi-database</v-icon>
              {{ entityName || 'Select entity' }}
            </v-chip>
          </select-entity>
          <div v-if="showCardinality" @click.stop>
            <toggle
              mandatory
              :value="props.query.schemaData?.schema?.cardinality || 'one-to-many'"
              :options="QUERY_CARDINALITY_OPTIONS"
              :disabled="store.state.isWorkflowDisabled"
              @update:modelValue="(val) => setReadModelCardinality(props.query.schemaId, val)"
            />
          </div>
        </div>
      </div>
    </template>
    <div v-else class="d-flex flex-column ga-1">
      <select-read-model-combobox
        :events="[props.event]"
        :cardIds="[props.query.id]"
        :style="{
          '--borderColor': props.query.isInContext ? cardTypeColor(queryCardType) : '#868686',
          '--borderTopWidth': '1px'
        }"
      />
      <div
        v-if="showDescription"
        :ref="setDescriptionRef"
        class="query-description"
        :class="{
          'query-description--empty': !linkedSchema?.description,
          'query-description--invalid': descriptionInvalid
        }"
        :contenteditable="!store.state.isWorkflowDisabled"
        :data-placeholder="store.state.isWorkflowDisabled ? '' : 'Add a short description…'"
        spellcheck="false"
        @input="onInputDescription"
        @paste="onPasteDescription"
        @blur="onBlurDescription"
        @keydown.enter.prevent="$event.target.blur()"
        @keydown.esc="restoreDescription($event)"
      >
        {{ linkedSchema?.description || '' }}
      </div>
      <div class="d-flex justify-space-between align-center ga-2">
        <select-entity
          :entityId="props.query.schemaData?.schema?.entityId"
          @onSelectEntity="(entity) => onSelectEntity(entity)"
        >
          <v-chip
            density="compact"
            size="small"
            class="fit-content entity-chip"
          >
            <v-icon start size="small">mdi-database</v-icon>
            {{ entityName || 'Select entity' }}
          </v-chip>
        </select-entity>
        <toggle
          v-if="showCardinality"
          mandatory
          :value="props.query.schemaData?.schema?.cardinality || 'one-to-many'"
          :options="QUERY_CARDINALITY_OPTIONS"
          :disabled="store.state.isWorkflowDisabled"
          @update:modelValue="(val) => setReadModelCardinality(props.query.schemaId, val)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import SelectReadModelCombobox from '@/components/SelectReadModelCombobox.vue'
import SelectEntity from '@/components/SelectEntity.vue'
import Toggle from '@/components/Toggle.vue'
import useDomainModel from '@/composables/useDomainModel'
import useReadModelActions from '@/composables/useReadModelActions'
import copyPasteHelpers from '@/helpers/copyPasteHelpers'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { SchemaType, QUERY_CARDINALITY_OPTIONS, MAX_DESCRIPTION_LENGTH } from '@/constants/schema'
import { sanitizeCardDescription } from '@/utils/sanitize'
import cardTypesApi from '@/api/cardTypesApi'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'

const props = defineProps({
  event: Object,
  query: Object,
  queryIndex: Number,
  editing: Boolean
})

const emit = defineEmits(['updateTextareaHeight', 'getTextareaRef', 'update:editing'])
const containerRef = ref(null)
const descriptionRef = ref(null)
const descriptionInvalid = ref(false)

const { cardTypeColor } = useDomainModel()
const { setReadModelCardinality, blurReadModelDescription } = useReadModelActions()

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const queryCardType = computed(() => diagramHelpers.methods.getQueryCardType(cardTypes.value))

const domainEventCardType = computed(() => diagramHelpers.methods.getDomainEventCardType(cardTypes.value))
const schemas = computed(() => workflow.value?.schemas ?? [])

const linkedSchema = computed(() => {
  if (!props.query.schemaId) {
    return null
  }

  return schemas.value.find((s) => s.id === props.query.schemaId) || null
})

const showCardinality = computed(() => (
  !!props.query.schemaId && linkedSchema.value?.type !== SchemaType.DOMAIN_EVENT && !!entityName.value
))

const showDescription = computed(() => (
  !!props.query.schemaId && linkedSchema.value?.type !== SchemaType.DOMAIN_EVENT
))

const readModelName = computed(() => {
  if (linkedSchema.value) {
    return linkedSchema.value.name
  }

  return sanitizeCardDescription(props.query.description || '')
})

const entityName = computed(() => {
  const entityId = props.query.schemaData?.schema?.entityId
  if (!entityId) {
    return null
  }

  const entity = schemas.value.find((s) => s.id === entityId)

  return entity?.name || null
})

const readModelDisplayColor = computed(() => {
  if (linkedSchema.value?.type === SchemaType.DOMAIN_EVENT && domainEventCardType.value) {
    return cardTypesApi.getBackgroundColour(domainEventCardType.value)
  }
  if (queryCardType.value) {
    return cardTypesApi.getBackgroundColour(queryCardType.value)
  }

  return 'inherit'
})

const setDescriptionRef = (el) => {
  descriptionRef.value = el
  emit('getTextareaRef', el)
}

const checkDescriptionLength = (el) => {
  descriptionInvalid.value = (el?.innerText || '').length > MAX_DESCRIPTION_LENGTH
}

const onInputDescription = (event) => {
  checkDescriptionLength(event.target)
  emit('updateTextareaHeight')
}

const onPasteDescription = (event) => {
  copyPasteHelpers.customPasteHandler(event)
  checkDescriptionLength(event.target)
  emit('updateTextareaHeight')
}

const onBlurDescription = async (event) => {
  const schema = linkedSchema.value
  if (!schema) {
    return
  }

  const newDescription = (event.target.innerText || '').trim()
  const currentDescription = schema.description || ''

  if (newDescription.length > MAX_DESCRIPTION_LENGTH) {
    store.commit('snackbar/showMessage', {
      content: `Description must be at most ${MAX_DESCRIPTION_LENGTH} characters`,
      timeout: 4000,
      color: 'red'
    }, { root: true })
    event.target.innerText = currentDescription
    descriptionInvalid.value = false

    return
  }

  descriptionInvalid.value = false
  await blurReadModelDescription(event, schema.id)
}

const restoreDescription = (event) => {
  event.target.innerText = linkedSchema.value?.description || ''
  descriptionInvalid.value = false
  event.target.blur()
}

const startEditing = () => {
  if (store.state.isWorkflowDisabled) {
    return
  }
  emit('update:editing', true)
}

const onClickOutside = (e) => {
  if (!props.editing) {
    return
  }
  if (containerRef.value?.contains(e.target)) {
    return
  }
  // Don't close if click is inside a combobox menu overlay
  if (e.target.closest('.v-overlay__content')) {
    return
  }

  emit('update:editing', false)
}

const onSelectEntity = async (entity) => {
  const schemaId = props.query.schemaId
  if (!schemaId) {
    return
  }

  const schemas = safeStructuredClone(workflow.value.schemas ?? [])
  const schemaIndex = schemas.findIndex((s) => s.id === schemaId)
  if (schemaIndex === -1) {
    return
  }

  schemas[schemaIndex].entityId = entity?.id || null

  await workflowApi.updateSchemas({
    schemas,
    workflow: workflow.value
  })
}

watch(() => props.editing, (val) => {
  if (val) {
    nextTick(() => document.addEventListener('mousedown', onClickOutside))
  } else {
    document.removeEventListener('mousedown', onClickOutside)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onClickOutside)
})
</script>

<style scoped>
.display-mode {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.display-label {
  font-weight: 500;
  line-height: 1.5;
  display: flex;
  align-items: center;
  height: 31px;
}

.display-label--name {
  font-size: 14px;
}

.entity-chip {
  cursor: pointer;
  height: 18px;
}

.query-description {
  min-height: 16px;
  padding: 2px 4px;
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: 11px;
  line-height: 18px;
  color: var(--text-light);
  outline: none;
  transition: border-color 0.2s;
  white-space: pre-wrap;
  word-break: break-word;
}

.query-description:focus {
  background-color: var(--light-2);
}

.query-description--empty:not(:focus)::before {
  content: attr(data-placeholder);
  color: var(--dark-1);
  pointer-events: none;
}

.query-description--invalid,
.query-description--invalid:focus {
  border-color: #ED1C0C;
}
</style>
